import {
  FC,
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';

import {
  DataPaginateResponse,
  PaginationRequestParams,
  ErrorResponse,
} from '~globals/types/api';
import { Nullable } from '~globals/types/commons';
import {
  useParseFiltersRequestSearchItems,
  useSelectionIds,
  useDataTableRequest,
  useToggleDialogConfirmation,
} from '~hooks/index';
import { ItemsSearchFilters } from '~hooks/useItemsSearchFilters';
import { searchItemsV2 } from '~services/item';
import {
  ItemFilterV2Base,
  ItemExtended,
  ItemsSearchHead,
} from '~services/item/types';

import { useDashboardVariantV2GiverContext } from '../../../Context';
// eslint-disable-next-line max-len
import { useDashboardVariantV2GiverInPreparationFiltersContext } from '../../../InPreparationFiltersContext';

import {
  DashboardVariantV2GiverContentInPreparationTabContextValue,
  DashboardVariantV2GiverContentInPreparationTabDialogDetailsOrderData,
} from './types';
import {
  DEFAULT_PAGINATION,
  DEFAULT_FILTERS_PAYLOAD,
  DEFAULT_ROUTE_ITEM_STATES,
  DEFAULT_GIVER_COLLECT_ITEM_STATES,
} from './utils';

const DashboardVariantV2GiverContentInPreparationTabContext =
  createContext<DashboardVariantV2GiverContentInPreparationTabContextValue>({
    parseFiltersRequest: () => DEFAULT_FILTERS_PAYLOAD,
    selection: {
      selectedIds: [],
      utils: {
        add: () => {},
        addMultiple: () => {},
        toggle: () => {},
        deleteOne: () => {},
        deleteMultiple: () => {},
        deleteCurrentAndAfter: () => {},
        has: () => false,
        isEmpty: () => true,
        reset: () => {},
      },
    },
    itemsWithGeoErrors: 0,
    table: {
      data: [],
      stats: null,
      loading: true,
      error: null,
      reloadRequest: () => {},
      totalSize: 0,
      queryParams: {
        pagination: DEFAULT_PAGINATION,
        filters: DEFAULT_FILTERS_PAYLOAD,
      },
      handleInitialData: () => {},
      handleChangeTable: () => {},
      handleInitialTable: () => {},
    },
    actions: {
      reloadDataAndResetSelectedRows: () => {},
      handleForceRequests: () => {},
    },
    dialogs: {
      detailsOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
    },
  });

export const useDashboardVariantV2GiverContentInPreparationTabContext =
  (): DashboardVariantV2GiverContentInPreparationTabContextValue =>
    useContext(DashboardVariantV2GiverContentInPreparationTabContext);

const DashboardVariantV2GiverContentInPreparationTabProvider: FC = ({
  children,
}) => {
  const {
    activeTab: { currentTab },
    statistics: { reloadData: reloadStatistics },
  } = useDashboardVariantV2GiverContext();
  const { filters, forceRequest } =
    useDashboardVariantV2GiverInPreparationFiltersContext();

  const parseFiltersRequest = useParseFiltersRequestSearchItems({
    filters: filters.filters,
    activeFilterTabId: currentTab,
    defaultRouteItemState: DEFAULT_ROUTE_ITEM_STATES,
    defaultGiverCollectItemStateTypesId: DEFAULT_GIVER_COLLECT_ITEM_STATES,
  });

  const selection = useSelectionIds<string>([]);

  const [itemsWithGeoErrors, setItemsWithGeoErrors] = useState(0);
  const table = useDataTableRequest<
    PaginationRequestParams<ItemFilterV2Base>,
    DataPaginateResponse<ItemExtended[], null, ItemsSearchHead>,
    ErrorResponse
  >({
    request: searchItemsV2,
    payload: {
      pagination: DEFAULT_PAGINATION,
      filters: parseFiltersRequest(),
    },
    transformResponse: (res) => {
      const response = res.data as DataPaginateResponse<
        ItemExtended[],
        null,
        ItemsSearchHead
      >;

      setItemsWithGeoErrors(response.head.itemsWithGeoErrors ?? 0);

      return response;
    },
  });
  const handleForceRequests = useCallback(
    (currentFilters?: Partial<ItemsSearchFilters>) => {
      selection.utils.reset();

      table.reloadRequest({
        pagination: table.queryParams.pagination,
        filters: parseFiltersRequest(currentFilters),
      });

      forceRequest.toggleValue(false);
    },
    [forceRequest, parseFiltersRequest, selection.utils, table],
  );

  useEffect(() => {
    if (forceRequest.value) {
      handleForceRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceRequest.value]);

  const reloadDataAndResetSelectedRows = useCallback(() => {
    selection.utils.reset();

    table.reloadRequest({
      pagination: table.queryParams.pagination,
      filters: parseFiltersRequest(),
    });

    reloadStatistics();
  }, [reloadStatistics, selection.utils, table, parseFiltersRequest]);

  const [
    openDialogDetailsOrder,
    selectedForDetailsOrder,
    handleOpenDialogDetailsOrder,
    handleCloseDialogDetailsOrder,
  ] =
    useToggleDialogConfirmation<
      Nullable<DashboardVariantV2GiverContentInPreparationTabDialogDetailsOrderData>
    >(null);

  return (
    <DashboardVariantV2GiverContentInPreparationTabContext.Provider
      value={{
        parseFiltersRequest,
        selection,
        itemsWithGeoErrors,
        table,
        actions: {
          reloadDataAndResetSelectedRows,
          handleForceRequests,
        },
        dialogs: {
          detailsOrder: {
            open: openDialogDetailsOrder,
            data: selectedForDetailsOrder,
            handleOpen: handleOpenDialogDetailsOrder,
            handleClose: handleCloseDialogDetailsOrder,
          },
        },
      }}
    >
      {children}
    </DashboardVariantV2GiverContentInPreparationTabContext.Provider>
  );
};

export default DashboardVariantV2GiverContentInPreparationTabProvider;
